import React, { useState } from 'react';
import { Minibar, Accommodation, StockItem, StockCategory } from '../types';
import { formatCurrencyValue } from '../utils/storage';
import { 
  Plus, 
  Columns, 
  Trash2, 
  Edit, 
  Eye, 
  Check, 
  X, 
  ArrowLeft, 
  Package, 
  TrendingUp, 
  Share2,
  FolderMinus,
  Sparkles
} from 'lucide-react';

interface MinibarsProps {
  minibars: Minibar[];
  accommodations: Accommodation[];
  stockItems: StockItem[];
  stockCategories: StockCategory[];
  onAddMinibar: (mb: Omit<Minibar, 'id' | 'items'>) => void;
  onUpdateMinibar: (id: string, mb: Partial<Minibar>) => void;
  onAddItemToMinibar: (minibarId: string, itemId: string, qty: number) => boolean;
  onRemoveItemFromMinibar: (minibarId: string, itemId: string, qty: number) => void;
  isAdmin: boolean;
}

export default function Minibars({
  minibars,
  accommodations,
  stockItems,
  stockCategories,
  onAddMinibar,
  onUpdateMinibar,
  onAddItemToMinibar,
  onRemoveItemFromMinibar,
  isAdmin
}: MinibarsProps) {
  // Navigation / detailed focus on 1 minibar
  const [activeMinibarId, setActiveMinibarId] = useState<string | null>(null);
  const [withdrawingItemId, setWithdrawingItemId] = useState<string | null>(null);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Inline edit state
  const [editingMinibarId, setEditingMinibarId] = useState<string | null>(null);
  const [editModel, setEditModel] = useState('');
  const [editTag, setEditTag] = useState('');
  const [editAccId, setEditAccId] = useState('');

  // Form state - Creating Minibar
  const [model, setModel] = useState('');
  const [tag, setTag] = useState('');
  const [accId, setAccId] = useState('');
  const [addError, setAddError] = useState('');

  // Form state - Transferring item from stock to minibar
  const [selectedCatId, setSelectedCatId] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [transferQty, setTransferQty] = useState('10');
  const [transferError, setTransferError] = useState('');

  const activeMinibar = minibars.find(m => m.id === activeMinibarId);

  // General add minibar
  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!model.trim() || !tag.trim() || !accId) {
      setAddError('Por favor, preencha todos os campos obrigatórios (*).');
      return;
    }
    const dup = minibars.some(m => m.tag.trim().toLowerCase() === tag.trim().toLowerCase());
    if (dup) {
      setAddError('Já existe um frigobar registrado com este Patrimônio.');
      return;
    }

    onAddMinibar({
      model: model.trim(),
      tag: tag.trim(),
      accommodationId: accId,
      active: true
    });

    setShowAddModal(false);
    setModel('');
    setTag('');
    setAddError('');
  };

  // Inline edit actions
  const startInlineEdit = (mb: Minibar) => {
    setEditingMinibarId(mb.id);
    setEditModel(mb.model);
    setEditTag(mb.tag);
    setEditAccId(mb.accommodationId);
  };

  const saveInlineEdit = (id: string) => {
    if (!editModel.trim() || !editTag.trim() || !editAccId) {
      alert('Campos não podem ser vazios.');
      return;
    }
    onUpdateMinibar(id, {
      model: editModel.trim(),
      tag: editTag.trim(),
      accommodationId: editAccId
    });
    setEditingMinibarId(null);
  };

  // Open transfer modal inside detailed view
  const openTransferModal = () => {
    const activeCats = stockCategories.filter(c => c.active);
    if (activeCats.length === 0) {
      alert("Nenhuma categoria ativa no estoque para transferir.");
      return;
    }
    const catId = activeCats[0].id;
    const catItems = stockItems.filter(i => i.categoryId === catId && i.active);

    setSelectedCatId(catId);
    setSelectedItemId(catItems[0]?.id || '');
    setTransferQty('10');
    setTransferError('');
    setShowTransferModal(true);
  };

  const handleCatChangeInTransfer = (catId: string) => {
    setSelectedCatId(catId);
    const catItems = stockItems.filter(i => i.categoryId === catId && i.active);
    setSelectedItemId(catItems[0]?.id || '');
  };

  const handleTransferSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeMinibarId || !selectedItemId || !transferQty) {
      setTransferError('Preencha as informações.');
      return;
    }
    const qtyNum = parseInt(transferQty);
    if (isNaN(qtyNum) || qtyNum <= 0) {
      setTransferError('Por favor, insira uma quantidade maior que zero.');
      return;
    }

    // Check central stock
    const item = stockItems.find(i => i.id === selectedItemId);
    if (!item) {
      setTransferError('Item selecionado inválido.');
      return;
    }
    if (qtyNum > item.quantity) {
      setTransferError(`Estoque insuficiente! Temos apenas ${item.quantity} un de ${item.name} em estoque.`);
      return;
    }

    // Apply transfer
    const success = onAddItemToMinibar(activeMinibarId, selectedItemId, qtyNum);
    if (success) {
      setShowTransferModal(false);
      setTransferError('');
    } else {
      setTransferError('Falha ao transferir o item.');
    }
  };

  return (
    <div className="space-y-6">
      {/* If focus minibar detailed page is NOT active */}
      {!activeMinibarId ? (
        <div className="space-y-6">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-5">
            <div>
              <h1 id="header-title-minibars" className="text-xl font-semibold tracking-tight text-zinc-950">
                Frigobares das Acomodações
              </h1>
              <p className="text-xs text-zinc-500 mt-1">
                Configure os frigobares físicos de cada acomodação e execute abastecimento ou retiradas de itens.
              </p>
            </div>
            <div>
              <button
                type="button"
                id="btn-add-minibar-trigger"
                onClick={() => {
                  const activeAccs = accommodations.filter(a => a.active);
                  if (activeAccs.length === 0) {
                    alert("Cadastre pelo menos uma acomodação ativa antes de criar frigobares!");
                    return;
                  }
                  setModel('');
                  setTag('');
                  setAccId(activeAccs[0].id);
                  setAddError('');
                  setShowAddModal(true);
                }}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer shadow-sm active:scale-[0.98]"
              >
                <Plus className="w-4 h-4" /> Novo Frigobar
              </button>
            </div>
          </div>

          {/* List of minibars in table */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
            
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table id="table-minibars" className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 font-bold uppercase tracking-wider text-xxs">
                    <th className="py-3 px-4 w-12 text-center">#</th>
                    <th className="py-3 px-4">Modelo</th>
                    <th className="py-3 px-4">Patrimônio</th>
                    <th className="py-3 px-4">Acomodação Vinculada</th>
                    <th className="py-3 px-4 text-center">Status</th>
                    <th className="py-3 px-4 text-right w-36">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200/60 font-medium text-zinc-700">
                  {minibars.length === 0 ? (
                    <tr>
                      <td colSpan={6} id="minibars-empty-row" className="p-8 text-center text-zinc-400 font-normal">
                        Nenhum frigobar cadastrado nas acomodações. Use + Novo Frigobar acima para iniciar.
                      </td>
                    </tr>
                  ) : (
                    minibars.map((mb, idx) => {
                      const isEditing = editingMinibarId === mb.id;
                      const linkedAcc = accommodations.find(a => a.id === mb.accommodationId);

                      return (
                        <tr key={mb.id} id={`minibar-row-${mb.id}`} className="hover:bg-zinc-50/50 transition-colors">
                          <td className="py-3.5 px-4 font-mono text-zinc-400 text-xxs text-center">{idx + 1}</td>
                          
                          {/* Modelo Column */}
                          <td className="py-3.5 px-4">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editModel}
                                onChange={(e) => setEditModel(e.target.value)}
                                className="text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-44"
                              />
                            ) : (
                              <span className="font-semibold text-zinc-900 text-xs">{mb.model}</span>
                            )}
                          </td>

                          {/* Patrimônio Column */}
                          <td className="py-3.5 px-4 font-mono text-xs">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editTag}
                                onChange={(e) => setEditTag(e.target.value)}
                                className="text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-28 font-mono"
                              />
                            ) : (
                              mb.tag
                            )}
                          </td>

                          {/* Acomodação Column */}
                          <td className="py-3.5 px-4">
                            {isEditing ? (
                              <select
                                value={editAccId}
                                onChange={(e) => setEditAccId(e.target.value)}
                                className="text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-44"
                              >
                                {accommodations.filter(a => a.active).map(a => (
                                  <option key={a.id} value={a.id}>{a.name} (Nº {a.number})</option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-zinc-600">
                                {linkedAcc ? `${linkedAcc.name} (Quarto ${linkedAcc.number})` : <span className="text-red-500 font-bold">Acomodação Inativa</span>}
                              </span>
                            )}
                          </td>

                          <td className="py-3.5 px-4 text-center">
                            <div className="flex justify-center">
                              {mb.active ? (
                                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-700 uppercase">Ativo</span>
                              ) : (
                                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-400 uppercase">Inativo</span>
                              )}
                            </div>
                          </td>

                          <td className="py-3.5 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {isEditing ? (
                                <button
                                  type="button"
                                  id={`btn-save-inline-mb-${mb.id}`}
                                  title="Salvar alterações"
                                  onClick={() => saveInlineEdit(mb.id)}
                                  className="p-1 px-1.5 bg-yellow-500 hover:bg-yellow-600 text-white border border-yellow-600 rounded cursor-pointer transition-colors shadow-sm flex items-center gap-1 text-[10px] uppercase font-bold"
                                >
                                  <Check className="w-3.5 h-3.5" /> Salvar
                                </button>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    id={`btn-edit-inline-mb-${mb.id}`}
                                    title="Editar informações do frigobar"
                                    onClick={() => startInlineEdit(mb)}
                                    className="p-1 text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded cursor-pointer transition-colors"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </button>
                                  
                                  <button
                                    type="button"
                                    id={`btn-view-minibar-detail-${mb.id}`}
                                    title="Visualizar itens do frigobar"
                                    onClick={() => setActiveMinibarId(mb.id)}
                                    className="p-1 text-blue-600 hover:text-blue-800 border border-blue-200 rounded cursor-pointer transition-colors bg-blue-50/20"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>

                                  <button
                                    type="button"
                                    id={`btn-toggle-minibar-${mb.id}`}
                                    onClick={() => {
                                      if (!isAdmin) {
                                        alert("Apenas administradores podem gerenciar frigobares.");
                                        return;
                                      }
                                      onUpdateMinibar(mb.id, { active: !mb.active });
                                    }}
                                    className="p-1 text-zinc-500 hover:text-zinc-700 hover:border-zinc-300 border border-zinc-200 dark:border-zinc-100 rounded text-xxs font-bold uppercase transition-colors"
                                  >
                                    {mb.active ? "Desativar" : "Ativar"}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card List View for Minibars list */}
            <div className="md:hidden divide-y divide-zinc-200">
              {minibars.length === 0 ? (
                <div id="minibars-empty-mobile" className="p-8 text-center text-zinc-400 font-normal text-xs bg-white">
                  Nenhum frigobar cadastrado nas acomodações. Use + Novo Frigobar acima para iniciar.
                </div>
              ) : (
                minibars.map((mb, idx) => {
                  const isEditing = editingMinibarId === mb.id;
                  const linkedAcc = accommodations.find(a => a.id === mb.accommodationId);

                  return (
                    <div key={mb.id} id={`minibar-card-mobile-${mb.id}`} className="p-4 bg-white space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-3">
                          {isEditing ? (
                            <div className="space-y-1.5 mb-2">
                              <label htmlFor={`edit-mob-model-${mb.id}`} className="block text-[9px] font-bold uppercase text-zinc-400">Modelo *</label>
                              <input
                                id={`edit-mob-model-${mb.id}`}
                                type="text"
                                value={editModel}
                                onChange={(e) => setEditModel(e.target.value)}
                                className="text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-full"
                              />
                            </div>
                          ) : (
                            <div className="font-semibold text-zinc-900 text-sm leading-tight">{mb.model}</div>
                          )}
                          <div className="text-[10px] text-zinc-400 font-normal mt-0.5">
                            Patrimônio: {isEditing ? (
                              <div className="mt-1">
                                <label htmlFor={`edit-mob-tag-${mb.id}`} className="block text-[9px] font-bold uppercase text-zinc-400 mb-0.5">Patrimônio *</label>
                                <input
                                  id={`edit-mob-tag-${mb.id}`}
                                  type="text"
                                  value={editTag}
                                  onChange={(e) => setEditTag(e.target.value)}
                                  className="text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-full font-mono"
                                />
                              </div>
                            ) : (
                              mb.tag
                            )}
                          </div>
                        </div>
                        <span className="text-xxs font-mono text-zinc-400 shrink-0">#{idx + 1}</span>
                      </div>

                      <div className="text-xs text-zinc-700 bg-zinc-55 bg-zinc-50 p-2.5 rounded-lg border border-zinc-150">
                        <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-1">Acomodação de Vínculo</span>
                        {isEditing ? (
                          <div className="mt-1">
                            <label htmlFor={`edit-mob-acc-${mb.id}`} className="sr-only">Escolha acomodação</label>
                            <select
                              id={`edit-mob-acc-${mb.id}`}
                              value={editAccId}
                              onChange={(e) => setEditAccId(e.target.value)}
                              className="text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-full bg-white font-semibold"
                            >
                              {accommodations.filter(a => a.active).map(a => (
                                <option key={a.id} value={a.id}>{a.name} (Nº {a.number})</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <span className="font-semibold text-zinc-805 text-zinc-800">
                            {linkedAcc ? `${linkedAcc.name} (Quarto ${linkedAcc.number})` : 'Acomodação Inativa'}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between border-t border-zinc-100 pt-2.5">
                        <div>
                          {mb.active ? (
                            <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-700 uppercase border border-green-200 animate-pulse">Ativo</span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-400 uppercase border border-zinc-200">Inativo</span>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5">
                          {isEditing ? (
                            <button
                              type="button"
                              id={`btn-mobile-save-inline-mb-${mb.id}`}
                              onClick={() => saveInlineEdit(mb.id)}
                              className="px-3.5 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-xxs font-bold uppercase cursor-pointer"
                            >
                              Salvar
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                id={`btn-mobile-edit-inline-mb-${mb.id}`}
                                title="Editar"
                                onClick={() => startInlineEdit(mb)}
                                className="p-1.5 text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-lg cursor-pointer bg-white"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              
                              <button
                                type="button"
                                id={`btn-mobile-view-minibar-detail-${mb.id}`}
                                onClick={() => setActiveMinibarId(mb.id)}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-blue-700 hover:text-blue-800 border border-blue-200 rounded-lg text-xxs font-bold uppercase cursor-pointer bg-blue-50"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                <span>Ver</span>
                              </button>

                              <button
                                type="button"
                                id={`btn-mobile-toggle-minibar-${mb.id}`}
                                onClick={() => {
                                  if (!isAdmin) {
                                    alert("Apenas administradores podem gerenciar frigobares.");
                                    return;
                                  }
                                  onUpdateMinibar(mb.id, { active: !mb.active });
                                }}
                                className="px-2.5 py-1.5 border border-zinc-200 rounded-lg text-xxs font-bold uppercase text-zinc-550 cursor-pointer hover:bg-zinc-50"
                              >
                                {mb.active ? 'Desativar' : 'Ativar'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

          </div>
        </div>
      ) : (
        /* Detailed Focus page for 1 minibar, matching page 8 & 9 */
        <div className="space-y-6">
          {/* Detailed Header */}
          <div className="flex items-center justify-between border-b border-zinc-200 pb-5">
            <button
              type="button"
              id="btn-back-minibar-list"
              onClick={() => setActiveMinibarId(null)}
              className="flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-950 font-semibold border border-zinc-200 px-3 py-1.5 rounded-lg bg-white cursor-pointer shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Voltar para lista
            </button>
            <span className="text-xxs font-bold text-zinc-400 uppercase tracking-wider">Painel Interno de Frigobar</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left Box: Dados do frigobar */}
            <div className="md:col-span-1 bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm h-fit">
              <div className="border-b border-zinc-100 bg-zinc-50 px-5 py-4">
                <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wider">
                  Dados do frigobar Nº {minibars.findIndex(m => m.id === activeMinibarId) + 1}
                </h3>
              </div>
              <div className="p-5 space-y-4 text-xs">
                <div className="border-b border-zinc-100 pb-2">
                  <span className="block text-[10px] font-bold text-zinc-400 uppercase">Modelo</span>
                  <span className="font-semibold text-zinc-900 text-sm">{activeMinibar?.model}</span>
                </div>
                <div className="border-b border-zinc-100 pb-2">
                  <span className="block text-[10px] font-bold text-zinc-400 uppercase">Patrimônio</span>
                  <span className="font-mono text-zinc-900 text-sm font-semibold">{activeMinibar?.tag}</span>
                </div>
                <div className="border-b border-zinc-100 pb-2">
                  <span className="block text-[10px] font-bold text-zinc-400 uppercase">Acomodação Vinculada</span>
                  <span className="font-semibold text-zinc-900 leading-normal">
                    {accommodations.find(a => a.id === activeMinibar?.accommodationId)?.name || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-zinc-400 uppercase">Total de Itens</span>
                  <span className="font-semibold text-zinc-900 text-xs text-orange-600">
                    {(() => {
                      const total = Array.isArray(activeMinibar?.items) ? activeMinibar.items.reduce((acc, i) => acc + i.quantity, 0) : 0;
                      return total === 1 ? '1 unidade cadastrada' : `${total} unidades cadastradas`;
                    })()}
                  </span>
                </div>
              </div>
            </div>

            {/* Right List: Itens disponíveis inside this minibar */}
            <div className="md:col-span-2 bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
              <div className="border-b border-zinc-100 bg-zinc-50 px-5 py-4 flex items-center justify-between">
                <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wider">
                  Itens Cadastrados no Frigobar
                </h3>
                <button
                  type="button"
                  id="btn-transfer-item-to-mb-trigger"
                  onClick={openTransferModal}
                  className="flex items-center gap-1 bg-green-700 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold uppercase cursor-pointer transition-all shadow-sm shadow-green-900/10"
                >
                  <Plus className="w-3.5 h-3.5" /> Adicionar item
                </button>
              </div>

              {/* Desktop Table for Inner Items */}
              <div className="hidden md:block overflow-x-auto">
                <table id="table-minibar-contained-items" className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-zinc-50/50 border-b border-zinc-200 text-zinc-500 font-bold uppercase tracking-wider text-xxs">
                      <th className="py-3 px-4 w-12 text-center">#</th>
                      <th className="py-3 px-4">Item</th>
                      <th className="py-3 px-4">Categoria</th>
                      <th className="py-3 px-4">Quantidade</th>
                      <th className="py-3 px-4">Valor unitário</th>
                      <th className="py-3 px-4 text-center">Status</th>
                      <th className="py-3 px-4 text-right w-24">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200/60 font-medium text-zinc-700">
                    {!Array.isArray(activeMinibar?.items) || activeMinibar.items.length === 0 ? (
                      <tr>
                        <td colSpan={7} id="minibar-items-empty-row" className="p-8 text-center text-zinc-400 font-normal">
                          Este frigobar encontra-se vazio no momento. Transfira produtos do estoque central usando "+ Adicionar Item".
                        </td>
                      </tr>
                    ) : (
                      activeMinibar.items.map((mItem, idx) => {
                        const originalProduct = stockItems.find(i => i.id === mItem.itemId);
                        const categoryName = originalProduct ? (stockCategories.find(c => c.id === originalProduct.categoryId)?.name || 'Outro') : 'N/D';

                        return (
                          <tr key={mItem.itemId} id={`mb-item-row-${mItem.itemId}`} className="hover:bg-zinc-50/50 transition-colors">
                            <td className="py-3 px-4 font-mono text-zinc-400 text-xxs text-center">{idx + 1}</td>
                            <td className="py-3 px-4">
                              <span className="font-semibold text-zinc-900 text-xs">{originalProduct?.name || 'Desconhecido'}</span>
                            </td>
                            <td className="py-3 px-4 text-zinc-500">{categoryName}</td>
                            <td className="py-3 px-4">
                              <span className="bg-orange-50 text-orange-850 px-2 py-0.5 rounded text-xs font-bold border border-orange-100 font-mono">
                                {mItem.quantity} un
                              </span>
                            </td>
                            <td className="py-3 px-4 font-mono">
                              {originalProduct ? formatCurrencyValue(originalProduct.price) : 'R$ 0,00'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex px-1.5 py-0.2 rounded text-[10px] font-bold bg-green-50 text-green-700">ATIVO</span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              {withdrawingItemId === mItem.itemId ? (
                                <div className="flex justify-end items-center gap-1.5">
                                  <span className="text-[10px] font-bold text-amber-800">Confirmar?</span>
                                  <button
                                    type="button"
                                    id={`btn-confirm-withdraw-${mItem.itemId}`}
                                    onClick={() => {
                                      onRemoveItemFromMinibar(activeMinibarId!, mItem.itemId, mItem.quantity);
                                      setWithdrawingItemId(null);
                                    }}
                                    className="px-2 py-0.5 bg-green-700 hover:bg-green-600 text-white rounded text-[9px] font-bold uppercase cursor-pointer transition-colors"
                                  >
                                    Sim
                                  </button>
                                  <button
                                    type="button"
                                    id={`btn-cancel-withdraw-${mItem.itemId}`}
                                    onClick={() => setWithdrawingItemId(null)}
                                    className="px-2 py-0.5 bg-zinc-200 hover:bg-zinc-300 text-zinc-700 rounded text-[9px] font-bold uppercase cursor-pointer transition-colors"
                                  >
                                    Não
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  id={`btn-withdraw-mb-item-${mItem.itemId}`}
                                  title="Retirar do frigobar e devolver ao estoque central"
                                  onClick={() => {
                                    setWithdrawingItemId(mItem.itemId);
                                  }}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 text-amber-700 hover:text-amber-850 border border-amber-200 bg-amber-50 hover:bg-amber-100 rounded-md font-bold text-[10px] uppercase cursor-pointer transition-colors"
                                >
                                  ↩ Retirar
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List for items inside focused Minibar */}
              <div className="md:hidden divide-y divide-zinc-200/80">
                {!Array.isArray(activeMinibar?.items) || activeMinibar.items.length === 0 ? (
                  <div id="minibar-items-empty-mobile" className="p-8 text-center text-zinc-400 font-normal text-xs bg-white">
                    Este frigobar encontra-se vazio no momento. Transfira produtos do estoque central usando "+ Adicionar Item".
                  </div>
                ) : (
                  activeMinibar.items.map((mItem, idx) => {
                    const originalProduct = stockItems.find(i => i.id === mItem.itemId);
                    const categoryName = originalProduct ? (stockCategories.find(c => c.id === originalProduct.categoryId)?.name || 'Outro') : 'N/D';

                    return (
                      <div key={mItem.itemId} id={`mb-item-card-mobile-${mItem.itemId}`} className="p-4 bg-white space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-zinc-900 text-sm">{originalProduct?.name || 'Desconhecido'}</div>
                            <div className="text-[10px] text-zinc-400 mt-0.5">Categoria: {categoryName} | Unitário: {originalProduct ? formatCurrencyValue(originalProduct.price) : 'R$ 0,00'}</div>
                          </div>
                          <span className="text-xxs font-mono text-zinc-400">#{idx + 1}</span>
                        </div>

                        <div className="flex items-center justify-between border-t border-zinc-100 pt-2.5">
                          <div>
                            <span className="bg-orange-50 text-orange-850 px-2.5 py-0.5 rounded text-[11px] font-bold border border-orange-200 font-mono">
                              {mItem.quantity} un
                            </span>
                          </div>
                          <div>
                            {withdrawingItemId === mItem.itemId ? (
                              <div className="flex items-center gap-1.5 bg-amber-50 p-1.5 rounded-lg border border-amber-200">
                                <span className="text-[10px] font-bold text-amber-800">Confirmar?</span>
                                <button
                                  type="button"
                                  id={`btn-mob-confirm-withdraw-${mItem.itemId}`}
                                  onClick={() => {
                                    onRemoveItemFromMinibar(activeMinibarId!, mItem.itemId, mItem.quantity);
                                    setWithdrawingItemId(null);
                                  }}
                                  className="px-2.5 py-1 bg-green-700 text-white rounded-md text-[10px] font-bold uppercase cursor-pointer"
                                >
                                  Sim
                                </button>
                                <button
                                  type="button"
                                  id={`btn-mob-cancel-withdraw-${mItem.itemId}`}
                                  onClick={() => setWithdrawingItemId(null)}
                                  className="px-2.5 py-1 bg-white text-zinc-700 border border-zinc-200 rounded-md text-[10px] font-bold uppercase cursor-pointer"
                                >
                                  Não
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                id={`btn-mobile-withdraw-mb-item-${mItem.itemId}`}
                                onClick={() => {
                                  setWithdrawingItemId(mItem.itemId);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-amber-700 hover:text-amber-850 border border-amber-200 bg-amber-50 rounded-lg font-bold text-xxs uppercase cursor-pointer"
                              >
                                ↩ Retirar
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Modal: CREATE NEW MINIBAR */}
      {showAddModal && (
        <div id="add-minibar-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 animate-fade-in overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-md lg:max-w-xl xl:max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Columns className="w-5 h-5 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Cadastrar Frigobar</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAdd} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {addError && (
                <div className="p-2.5 bg-red-50 text-red-700 text-xxs font-semibold border border-red-100 rounded-lg">
                  {addError}
                </div>
              )}

              <div>
                <label htmlFor="form-mb-model" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Modelo *</label>
                <input
                  type="text"
                  id="form-mb-model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  placeholder="Ex: Brastemp Retro Vermelha"
                  required
                />
              </div>

              <div>
                <label htmlFor="form-mb-tag" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Patrimônio *</label>
                <input
                  type="text"
                  id="form-mb-tag"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none focus:ring-1 focus:ring-zinc-900 text-normal font-mono"
                  placeholder="Ex: FR-RET-01"
                  required
                />
              </div>

              <div>
                <label htmlFor="form-mb-acc" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Acomodação *</label>
                <select
                  id="form-mb-acc"
                  value={accId}
                  onChange={(e) => setAccId(e.target.value)}
                  className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none font-semibold text-zinc-600"
                >
                  {accommodations.filter(a => a.active).map(a => (
                    <option key={a.id} value={a.id}>{a.name} (Nº {a.number})</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  id="btn-submit-minibar-add"
                  className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm"
                >
                  Cadastrar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: TRANSFER PRODUCTS FROM CENTRAL STOCK */}
      {showTransferModal && (
        <div id="transfer-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 animate-fade-in overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-md lg:max-w-xl xl:max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Adicionar item ao frigobar #{minibars.findIndex(m => m.id === activeMinibarId) + 1}</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowTransferModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleTransferSubmit} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {transferError && (
                <div className="p-2.5 bg-red-50 text-red-700 text-xxs font-semibold border border-red-100 rounded-lg">
                  {transferError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="trans-cat" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Categoria</label>
                  <select
                    id="trans-cat"
                    value={selectedCatId}
                    onChange={(e) => handleCatChangeInTransfer(e.target.value)}
                    className="w-full text-xs py-2 px-2 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/35 focus:outline-none font-semibold text-zinc-650"
                  >
                    {stockCategories.filter(c => c.active).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="trans-item" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Insumo / Prd</label>
                  <select
                    id="trans-item"
                    value={selectedItemId}
                    onChange={(e) => setSelectedItemId(e.target.value)}
                    className="w-full text-xs py-2 px-2 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/35 focus:outline-none font-semibold text-zinc-650"
                  >
                    {stockItems.filter(i => i.categoryId === selectedCatId && i.active).map(i => (
                      <option key={i.id} value={i.id}>{i.name} (Saldo: {i.quantity})</option>
                    ))}
                    {stockItems.filter(i => i.categoryId === selectedCatId && i.active).length === 0 && (
                      <option value="" disabled>--- Vazio ---</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Show item read-only stock details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xxs font-bold text-zinc-400 uppercase tracking-wide mb-1">Em estoque</label>
                  <input
                    type="text"
                    disabled
                    value={(() => {
                      const item = stockItems.find(i => i.id === selectedItemId);
                      return item ? `${item.quantity} unidades` : 'Nenhum';
                    })()}
                    className="w-full text-xs py-2 px-3 border border-zinc-100 rounded-lg text-zinc-400 bg-zinc-100/60 focus:outline-none font-mono"
                  />
                </div>

                <div>
                  <label htmlFor="input-trans-qty" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Adicionar *</label>
                  <input
                    type="number"
                    id="input-trans-qty"
                    value={transferQty}
                    onChange={(e) => setTransferQty(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/35 focus:outline-none text-center font-bold font-mono"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  id="btn-confirm-transfer-mb"
                  className="flex items-center gap-1 px-5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm shadow-green-950/10"
                >
                  Abastecer Frigobar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
